import { AmountValue, Inflation } from 'common/interfaces';
import { Currency } from 'enums';
import { DepositFrequency } from 'stores/workflowStores/finances/depositsStore/model';
import { DateTime } from 'luxon';

export enum ExtProdSortBy {
  TITLE = 'title',
  INST = 'finInstTitle',
  BALANCE = 'balance',
}

export enum ExtProductCategory {
  EDWARD = 'EDWARD',
  FINANCIAL_PRODUCT = 'FINANCIAL_PRODUCT',
  PROPERTY = 'PROPERTY',
  LOAN = 'LOAN',
  NONE = '',
}

export enum ExtProductType {
  INVESTMENT = 'INVESTMENT',
  LTIP = 'LTIP',
  RETIREMENT = 'RETIREMENT',
  BUILDING = 'BUILDING',
  LIFE = 'LIFE',
  ACCOUNT = 'ACCOUNT',
  OTHER = 'OTHER',

  REAL_ESTATE = 'REAL_ESTATE',
  REAL_ESTATE_RENT = 'REAL_ESTATE_RENT',
  COMPANY = 'COMPANY',
  VALUABLES = 'VALUABLES',
  OTHER_PROPERTY = 'OTHER_PROPERTY',

  MORTGAGE = 'MORTGAGE',
  CONSUMER_LOAN = 'CONSUMER_LOAN',
  BUILDING_LOAN = 'BUILDING_LOAN',
  OTHER_LOAN = 'OTHER_LOAN',

  NONE = '',

  CURRENT = 'CURRENT'
}

export enum ExtProductFreq {
  SINGLE = 'SINGLE',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  REPEAT_EVERY = 'REPEAT_EVERY',
  INFINITE = 'INFINITE',
  ADVANCE_PENSION = 'ADVANCE_PENSION',
  PENSION = 'PENSION',
  NONE = '',
}

export enum ExtProductUsage {
  TARGET = 'TARGET',
  EXTERNAL = 'EXTERNAL',
  NONE = 'NONE',
}

export enum ExtProductMode {
  MANUAL = 'MANUAL',
  CALCULATED = 'CALCULATED',
  NONE = '',
}

export enum ExtProductAsset {
  STOCK = 'STOCK',
  BOND = 'BOND',
  REAL_ESTATE = 'REAL_ESTATE',
  COMMODITY = 'COMMODITY',
  ALTERNATIVE = 'ALTERNATIVE',
  CASH = 'CASH',
  UNKNOWN = 'UNKNOWN',
  CRYPTO = 'CRYPTO',
  NONE = '',
}

export enum ExtProductRegion {
  CENTRAL_EUROPE = 'CZECH',
  EUROPE = 'EUROPE',
  NORTH_AMERICA = 'NORTH_AMERICA',
  ASIA = 'ASIA',
  EMERGING = 'EMERGING',
  UNKNOWN = 'UNKNOWN',
  OTHER = 'OTHER',
  NONE = '',
}

export interface ExternalProductOptions {
  asset: { [key in ExtProductType]: ExtProductAsset[] };
  currency: Currency[];
  frequency: { [key in ExtProductType]: ExtProductFreq[] };
  mode: ExtProductMode[];
  region: ExtProductRegion[];
  type: { [key in ExtProductCategory]: ExtProductType[] };
  usage: { [key in ExtProductCategory]: ExtProductUsage[] };
  interest: { [key in ExtProductType]: number };
  maxDate: string;
  minYears: number;
  maxYears: number;
  inflationType: string[];
}

export enum BalanceDataType {
  LOAN = 'LOAN',
  OWN_PROPERTY = 'OWN_PROPERTY',
  TOTAL = 'TOTAL',
}

export interface BalanceData {
  [key: string]: number[];
}

export type BalanceDataset = { [key in BalanceDataType]: BalanceData[] };

export interface BalanceSheetData {
  dataset: BalanceDataset;
  summary: number[];
}

export interface ProductsSortState {
  index?: number; // table
  direction?: boolean; // table
  sortBy?: ExtProdSortBy; // cards
  orderDir?: boolean; // cards
}

export interface ExternalProductsData {
  externalProducts: ExternalProductData[];
  addingEnabled: boolean;
  options: ExternalProductOptions;
  balanceSheet: BalanceSheetData;
  tableLayout: boolean;
  sort: ProductsSortState;
  currentDeposits: AmountValue;
  summary: {
    balance: AmountValue;
    deposit: AmountValue;
  }
}

export interface FinancialInstitution {
  id: string;
  title: string;
  internal: boolean;
  colour?: string;
  logo?: string;
  icon?: string;
}

export interface SimpleDeposit {
  value?: AmountValue;
  frequency?: DepositFrequency;
  repeatYears?: string;
  since?: string;
  until?: string;
  aggregated?: boolean;
}

export interface ExternalProductData {
  id: string; // string ve stylu cílů, nebo BÚ, prostě si ho posílám tam a zpátky.
  financialInstitution?: FinancialInstitution; // - objekt který obsahuje IDčko, název, flag internal (a popř. barvu, ikonu, logo - to mi ideálně neposílej v POSTu a PATCHi) finanční instituce, které se to týká. Flag internal slouží k rozlišení "Edward" instituce a ostatních institucí. Když je internal=true, tak umožňuješ propojení účtu a já podle toho validuju, že to propojení vyžaduju. Pokud tam je cokoliv jiného, je to false a propojení není možné.
  accountId?: string; // - propojené account ID účtu, pouze pokud vybraná fin.instituce má internal=true. Účet musí být validní a dostupný, tj. musí být v seznamu vráceném výše uvedeným endpointem a musí mít flag available=true. Povinný.
  invalidLink?: boolean; // napojeny ucet jiz neni validni
  title: string; //- název cizího produktu. Povinný.
  active?: boolean; // - flag, který řídí, jestli produkt nějak řešíme nebo ne, pro tebe platí, že když je false, ignoruješ celou entitu
  category: ExtProductCategory;
  type: ExtProductType; //  - číselník typů - INVESTMENT, LTIP, RETIREMENT (DPS/PP), BUILDING (stavebko), LIFE (životko), ACCOUNT (bank.účet), OTHER. Povinný. Řídí spoustu věcí v dopočítávání, tj. při změně je potřeba volat přepočty (PATCH) a má vliv na některé další věci, např. v options.
  balance: AmountValue; // - jednorázový vklad/aktuální zůstatek. Povinný
  periodic?: SimpleDeposit; // - vklad klienta, objekt s datovou strukturou stejnou jako má následný vklad (tedy obsahuje amount, since, until...). Povinný
  employer?: SimpleDeposit; //- vklad zaměstnavatele, stejná struktura jako vklad klienta, ale relevantní je jen pro určité DIP a DPS/PP
  government?: SimpleDeposit; // - státní příspěvek, stejná struktura jako vklad klienta, ale relevantní je jen pro stavebko a DPS/PP
  taxDeduction: AmountValue; // - daňový odpočet, hodnota se počítá automaticky, ale jde přepsat
  interest: number; // - výnos p.a - hodnota má default (chodí v options) ale lze upravit libovolně. Povinný.
  lastUpdate: string; // - datum poslední aktualizace. Může být null v případě propojeného produktu, jinak bude vždy povinný
  inflation: Inflation; // objekt stejný jako je u cílů, vkladů se stejnou logikou a strukturou
  frequency: ExtProductFreq; //  - číselník frekvencí - SINGLE, MONTHLY, YEARLY, REPEAT_EVERY, INFINITE (neomezeno), ADVANCE_PENSION (předdůchod), PENSION (důchod). Pozor, řídí se poměrně komplikovanou logikou, posílám ti mapování v options. Povinný.
  repeatYears: number; // - pouze pro frekvenci REPEAT_EVERY, tehdy povinný, jinak nevyplňovat.
  children: number; // - pouze pro důchody a předdůchody, tehdy povinné, integer. Jinak neviditelné. Může přijít předvyplněné podle renty.
  retirementAge?: { years: number; months: number }; //  - read only field dopočítaného důchodového věku, pokud je relevantní.
  usage: ExtProductUsage; // - číselník způsobů použití - TARGET (cíle do plánu), EXTERNAL (jiný), NONE (nevyplňovat) . Povinné. Pokud type=NONE, vše níže uvedené skrýt a nevyplňovat.
  targetTitle: string; // - název jiného cíle, relevantní a povinné pouze pokud usage=EXTERNAL, jinak skrýt.
  mode: ExtProductMode; // - způsob nastavení částky, hodnoty CALCULATED (default) nebo MANUAL (pouze pro jednorázovou frekvenci), řídí editovatelnost pole částka, detail viz zadání. Povinné.
  value: AmountValue; // - dopočítaná částka, lze přepsat pokud mode=MANUAL jinak read only. Default je prázdný, nutno dopočítat tlačítkem. Povinné.
  since: string; // - hodnota "od". Povinné pokud je viditelné.
  until: string; // - hodnota "do". Povinné pokud je viditelné.
  asset: { [id in ExtProductAsset]?: number }; // - mapa rozložení podle aktiv, chodí ve formátu string-číslo kde string = klíč který musíš překládat slovníkem a hodnota je des.číslo 0-1. Poslední hodnotou je UNKNOWN, která odpovídá "nerozděleno" a je needitovatelná a měla by odpovídat rozdílu součtu ostatních a 100%. Hodnoty: STOCK, BOND, REAL_ESTATE, COMMODITY, ALTERNATIVE, CASH, UNKNOWN. Povinné a součet musí odpovídat 1.
  currency: { [id in Currency]?: number }; // - mapa rozložení podle měn, platí vše jako pro asset. Hodnoty: CZK, EUR, USD, UNKNOWN
  region: { [id in ExtProductRegion]?: number }; // - mapa rozložení podle reigonů, platít vše jako pro asset. Hodnoty: CZECH, EUROPE, NORTH_AMERICA, ASIA, EMERGING, UNKNOWN
  appliedValues?: boolean;
  useValues?: boolean;
  includedInChart?: boolean;

  usePeriodic?: boolean; //  PROPERTY
  useTarget?: boolean; //  PROPERTY
}

export const getInitialValues = (currency: Currency, interest?: number) => ({
  financialInstitution: { id: '', title: '', internal: false },
  id: '',
  // accountId: '',
  category: ExtProductCategory.NONE,
  title: '',
  type: ExtProductType.NONE,
  balance: { value: 0, currency: currency },
  periodic: { value: { value: 0, currency: currency } },
  employer: { value: { value: 0, currency: currency } },
  government: { value: { value: 0, currency: currency } },
  taxDeduction: { value: 0, currency: currency },
  interest: 0, //interest ? f2p(interest) :
  //lastUpdate: DateTime.local().toString(),
  lastUpdate: DateTime.local().toFormat('yyyy-MM-dd').toString(),
  inflation: { enabled: true, rate: 0, valuation: false, type: '' },
  frequency: ExtProductFreq.MONTHLY,
  repeatYears: 0,
  children: 0,
  usage: ExtProductUsage.TARGET,
  targetTitle: '',
  mode: ExtProductMode.CALCULATED,
  value: { value: 0, currency: currency },
  since: '',
  until: '',
  asset: {},
  currency: {},
  region: {},
  accountCurrency: currency,
  //appliedValues: false,
  useValues: false,
});

export interface LinkableAccount {
  title: string;
  variableSymbol: string;
  accountId: string;
  available: boolean;
  clientName?: string;
}

export const tableCategories = [
  {
    title: ExtProductCategory.EDWARD as string,
    id: ExtProductCategory.EDWARD as string,
  },
  {
    title: ExtProductCategory.FINANCIAL_PRODUCT as string,
    id: ExtProductCategory.FINANCIAL_PRODUCT as string,
  },
  {
    title: ExtProductCategory.PROPERTY as string,
    id: ExtProductCategory.PROPERTY as string,
  },
  {
    title: ExtProductCategory.LOAN as string,
    id: ExtProductCategory.LOAN as string,
  },
  {
    title: '',
    id: 'SUM',
  },
];

export const defaultCardSort = { sortBy: ExtProdSortBy.INST, orderDir: true };
